import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";

export default function SignupPage() {
  const { user, registerTenant, loading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ company_name: "", full_name: "", email: "", password: "", industry: "SaaS", country: "India", logo_url: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user) {
    return <Navigate to={user.role === "super_admin" ? "/admin" : "/app"} replace />;
  }

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_000_000) { setErr("Logo must be under 1MB."); return; }
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, logo_url: String(r.result) }));
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setBusy(true);
    const r = await registerTenant(form);
    if (r.ok && form.logo_url) {
      // Save logo via branding endpoint after registration
      try {
        const { api } = await import("@/lib/api");
        await api.put("/tenant/branding", { logo_url: form.logo_url });
      } catch (_) {}
    }
    setBusy(false);
    if (!r.ok) setErr(r.error);
    else nav("/app", { replace: true });
  };

  return (
    <div className="min-h-screen brand-radial">
      <div className="px-6 py-6"><Link to="/"><Logo /></Link></div>
      <div className="max-w-xl mx-auto px-6 mt-6">
        <Card className="p-8 border-slate-100 shadow-elev-2">
          <h1 className="text-2xl font-extrabold text-[#1F3A6B]">Create your workspace</h1>
          <p className="text-sm text-slate-600 mt-1">Start free. Invite teammates anytime. No credit card required.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-[#1F3A6B]">Company name</Label>
                <Input data-testid="signup-company" value={form.company_name} onChange={upd("company_name")} required className="mt-1.5 h-11"/>
              </div>
              <div>
                <Label className="text-sm font-semibold text-[#1F3A6B]">Your name</Label>
                <Input data-testid="signup-name" value={form.full_name} onChange={upd("full_name")} required className="mt-1.5 h-11"/>
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#1F3A6B]">Work email</Label>
              <Input data-testid="signup-email" type="email" value={form.email} onChange={upd("email")} required className="mt-1.5 h-11" placeholder="you@company.com"/>
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#1F3A6B]">Password</Label>
              <Input data-testid="signup-password" type="password" value={form.password} onChange={upd("password")} required minLength={8} className="mt-1.5 h-11" placeholder="At least 8 characters"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-[#1F3A6B]">Industry</Label>
                <select data-testid="signup-industry" value={form.industry} onChange={upd("industry")} className="mt-1.5 h-11 w-full rounded-md border border-slate-200 px-3 bg-white">
                  {["SaaS","Retail","Hospitality","Banking","Healthcare","Telecom","Other"].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-[#1F3A6B]">Country</Label>
                <Input data-testid="signup-country" value={form.country} onChange={upd("country")} className="mt-1.5 h-11"/>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1F3A6B]">Brand logo (optional)</Label>
              <div className="mt-1.5 flex items-center gap-3">
                <div className="h-14 w-14 rounded-md border-2 border-dashed border-slate-200 bg-slate-50 grid place-items-center overflow-hidden">
                  {form.logo_url
                    ? <img src={form.logo_url} alt="" className="max-h-12 max-w-12 object-contain"/>
                    : <span className="text-[10px] text-slate-400 font-bold">LOGO</span>
                  }
                </div>
                <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-[#F58220] hover:text-[#1F3A6B] font-semibold">
                  Upload (PNG/SVG, ≤1MB)
                  <input data-testid="signup-logo-file" type="file" accept="image/*" className="hidden" onChange={onPickLogo}/>
                </label>
                {form.logo_url && (
                  <button type="button" onClick={() => setForm((p) => ({ ...p, logo_url: "" }))} className="text-xs text-rose-600 hover:underline">Remove</button>
                )}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Shown on every survey header & footer. You can change this later in Settings → Brand & Logo.</p>
            </div>
            {err && <p data-testid="signup-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">{err}</p>}
            <Button data-testid="signup-submit" disabled={busy} type="submit" className="w-full h-11 bg-[#F58220] hover:bg-[#E36F0E] text-white">{busy ? "Creating workspace…" : "Create workspace"}</Button>
          </form>

          <p className="text-sm text-slate-600 mt-5 text-center">
            Already have an account? <Link to="/login" className="text-[#F58220] font-semibold">Sign in</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
